import MalKort from '../components/MalKort';
import React, { useState, useEffect, useMemo } from 'react';
import { Clock, SubNav, Metric, TrendCompare, FjolRad, shiftISO, VisaFlerRad } from '../components/SharedComponents';
import { getISOWeek } from '../utils';
import { getLanthandel, getLanthandelProdukter } from '../qlik';

// Stigmyhrs Lanthandel — obemannad livsmedelsbutik i Ramundberget
// (dotterbolaget Stigmyhrs Livs). Datakälla: HonestBox — dagsserien via
// cache (full historik sedan öppningen dec 2025), produktlistan live.
const COLOR = '#3D6B21';

const kr = (v) => (v == null || isNaN(v)) ? '–' : Math.round(v).toLocaleString('sv-SE') + ' kr';
const p2 = (n) => String(n).padStart(2, '0');
const todayISO = () => { const t = new Date(); return `${t.getFullYear()}-${p2(t.getMonth() + 1)}-${p2(t.getDate())}`; };
const chip = (active) => ({
  background: active ? COLOR : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
  fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
});
const dateInp = {
  border: '1px solid var(--border)', borderRadius: 8, padding: '3px 8px',
  fontSize: 11.5, fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)',
};

// ── PRODUKTER — sålda enheter per period (beställningsunderlag) ────
function ProdukterTab({ faktor = 1 }) {
  const [period, setPeriod] = useState('idag');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sortBy, setSortBy] = useState('antal');
  const [visaAlla, setVisaAlla] = useState(false);
  const [sok, setSok] = useState('');
  const [res, setRes] = useState(undefined);
  const [err, setErr] = useState(null);

  const custom = !!(from && to && from <= to);
  const range = useMemo(() => {
    if (custom) return { from, to };
    const t = todayISO();
    if (period === 'idag')  return { from: t, to: t };
    if (period === 'igar')  { const g = shiftISO(t, -1); return { from: g, to: g }; }
    if (period === 'vecka') { const d = new Date(t + 'T12:00:00Z'); const dow = (d.getUTCDay() + 6) % 7; return { from: shiftISO(t, -dow), to: t }; }
    return { from: t.slice(0, 8) + '01', to: t }; // månad
  }, [custom, from, to, period]);

  useEffect(() => {
    let live = true;
    setRes(undefined); setErr(null); setVisaAlla(false);
    getLanthandelProdukter(range.from, range.to)
      .then((r) => { if (live) setRes(r); })
      .catch((e) => { if (live) setErr(e.message || 'Hämtningen misslyckades'); });
    return () => { live = false; };
  }, [range.from, range.to]);

  // Sök filtrerar på artikelnamn (t.ex. "mjölk" → alla mjölkvarianter).
  // faktor > 1 = ex moms-läget; beloppen kommer inkl moms från HonestBox.
  const produkter = useMemo(() => {
    const q = sok.trim().toLowerCase();
    return [...(res?.produkter || [])]
      .filter((p) => !q || (p.namn || '').toLowerCase().includes(q))
      .map((p) => (faktor === 1 ? p : { ...p, belopp: Math.round(p.belopp / faktor) }))
      .sort((a, b) => b[sortBy] - a[sortBy]);
  }, [res, sortBy, sok, faktor]);
  const shown = sok.trim() || visaAlla ? produkter : produkter.slice(0, 15); // sökning visar alltid alla träffar
  const maxVal = Math.max(...shown.map((p) => p[sortBy] || 0), 1);
  const sumAntal  = produkter.reduce((s, p) => s + p.antal, 0);
  const sumBelopp = produkter.reduce((s, p) => s + p.belopp, 0);

  return (
    <>
      {/* Periodval + datumsök */}
      <div className="card" style={{ paddingTop: 12, paddingBottom: 12 }}>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', fontFamily: 'system-ui,sans-serif' }}>
          {[['idag', 'Idag'], ['igar', 'Igår'], ['vecka', 'Denna vecka'], ['manad', 'Denna månad']].map(([id, lab]) => (
            <button key={id} onClick={() => { setPeriod(id); setFrom(''); setTo(''); }} style={chip(period === id && !custom)}>{lab}</button>
          ))}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 3px' }} />
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={dateInp} aria-label="Från datum" />
          <span style={{ color: 'var(--text-secondary)', fontSize: 11.5 }}>–</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={dateInp} aria-label="Till datum" />
          {custom && <button onClick={() => { setFrom(''); setTo(''); }} style={chip(false)}>Rensa</button>}
        </div>
      </div>

      {/* KPI:er för perioden */}
      <div className="grid-4">
        <Metric label="Sålda produkter" value={res ? res.antalSalda.toLocaleString('sv-SE') : '…'} sub={`${range.from}${range.from !== range.to ? ` → ${range.to}` : ''}`} />
        <Metric label="Försäljningsvärde" value={res ? kr(res.varde / faktor) : '…'} sub={faktor === 1 ? 'Inkl. moms' : 'Ex. moms'} />
        <Metric label="Unika artiklar" value={res ? String(res.produkter.length) : '…'} sub="Olika produkter sålda" />
        <Metric label="Insläpp (BankID)" value={res && res.besok != null ? String(res.besok) : '–'}
          sub={res && res.besok > 0 && res.antalSalda > 0 ? `${(res.antalSalda / res.besok).toFixed(1)} produkter/besök` : 'Identifieringar vid dörren'} />
      </div>

      {/* Produktlistan */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Sålda produkter — hela listan</div>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="search" value={sok} onChange={(e) => setSok(e.target.value)}
              placeholder="Sök artikel (t.ex. mjölk)…"
              style={{ ...dateInp, width: 180, padding: '5px 10px' }}
            />
            <button onClick={() => setSortBy('antal')} style={chip(sortBy === 'antal')}>Antal</button>
            <button onClick={() => setSortBy('belopp')} style={chip(sortBy === 'belopp')}>Belopp</button>
          </div>
        </div>
        {err && <div style={{ fontSize: 13, color: '#A32D2D', fontFamily: 'system-ui,sans-serif' }}>{err}</div>}
        {!err && res === undefined && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Hämtar live från HonestBox…</div>}
        {!err && res && produkter.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen försäljning i perioden.</div>}
        {!err && res && shown.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
            <div style={{ width: 22, fontSize: 10, color: '#bbb', textAlign: 'right', fontFamily: 'system-ui,sans-serif', flexShrink: 0 }}>{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 11.5, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.namn}</span>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', flexShrink: 0, marginLeft: 8 }}>
                  {sortBy === 'antal' ? `${p.antal} st · ${kr(p.belopp)}` : `${kr(p.belopp)} · ${p.antal} st`}
                </span>
              </div>
              <div style={{ height: 3, background: 'var(--panel2)', borderRadius: 2 }}>
                <div style={{ height: '100%', width: `${Math.round(p[sortBy] / maxVal * 100)}%`, background: COLOR, borderRadius: 2 }} />
              </div>
            </div>
          </div>
        ))}
        {!err && res && !sok.trim() && <VisaFlerRad total={produkter.length} initial={15} expanded={visaAlla} onToggle={() => setVisaAlla(v => !v)} />}
        {!err && res && produkter.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 8, borderTop: '2px solid var(--border)', fontSize: 12.5, fontWeight: 700, fontFamily: 'system-ui,sans-serif' }}>
            <span>Summa{sok.trim() ? ` "${sok.trim()}"` : ''} — {produkter.length} artiklar</span>
            <span>{sumAntal.toLocaleString('sv-SE')} st · {kr(sumBelopp)}</span>
          </div>
        )}
        <div className="note">
          Live från HonestBox — 1 rad per såld enhet. Belopp {faktor === 1 ? 'inkl. moms'
            : 'omräknade till ex. moms med den blandade satsen, inte artikelns egen'}.
        </div>
      </div>
    </>
  );
}

// ── FÖRSÄLJNING — perioder & jämförelse mot tidigare år ───────────
function ForsaljningTab({ days }) {
  const [period, setPeriod] = useState('30d'); // '30d' | '90d' | '12m'
  const [gran, setGran] = useState('dag');     // 'dag' | 'vecka' | 'manad'
  const [matt, setMatt] = useState('oms');     // 'oms' | 'kop' | 'snitt'
  const [fromSel, setFromSel] = useState('');
  const [toSel, setToSel] = useState('');

  const byDay = useMemo(() => {
    const m = {};
    for (const [datum, oms, , kvitton] of (days || [])) m[datum] = { oms: oms || 0, kop: kvitton || 0 };
    return m;
  }, [days]);

  const custom = !!(fromSel && toSel && fromSel <= toSel);
  const MANADER = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

  const data = useMemo(() => {
    const t = todayISO();
    const daysBack = period === '30d' ? 29 : period === '90d' ? 89 : 364;
    const fromStr = custom ? fromSel : shiftISO(t, -daysBack);
    const endStr = custom ? (toSel > t ? t : toSel) : t;
    const span = Math.round((new Date(endStr) - new Date(fromStr)) / 86400000);
    const granEff = (gran === 'dag' && span > 92) ? 'vecka' : gran;

    const buckets = [];
    if (granEff === 'dag') {
      for (let d = fromStr; d <= endStr; d = shiftISO(d, 1)) buckets.push({ label: `${Number(d.slice(8, 10))}/${Number(d.slice(5, 7))}`, from: d, to: d });
    } else if (granEff === 'vecka') {
      const fd = new Date(fromStr + 'T12:00:00Z');
      let mon = shiftISO(fromStr, -((fd.getUTCDay() + 6) % 7));
      while (mon <= endStr) { buckets.push({ label: `v${getISOWeek(new Date(mon + 'T12:00:00Z'))}`, from: mon, to: shiftISO(mon, 6) }); mon = shiftISO(mon, 7); }
    } else {
      let ym = Number(fromStr.slice(0, 4)) * 100 + Number(fromStr.slice(5, 7));
      const endYm = Number(endStr.slice(0, 4)) * 100 + Number(endStr.slice(5, 7));
      while (ym <= endYm) {
        const y = Math.floor(ym / 100), m = ym % 100;
        buckets.push({ label: MANADER[m - 1], from: `${y}-${p2(m)}-01`, to: `${y}-${p2(m)}-${p2(new Date(y, m, 0).getDate())}`,
          pf: `${y - 1}-${p2(m)}-01`, pt: `${y - 1}-${p2(m)}-${p2(new Date(y - 1, m, 0).getDate())}` });
        ym = m === 12 ? (y + 1) * 100 + 1 : ym + 1;
      }
    }

    const sum = (key, f, tt) => { let s = 0; for (let d = f; d <= tt; d = shiftISO(d, 1)) s += byDay[d]?.[key] || 0; return s; };
    const rows = buckets.map((b) => {
      const pf = b.pf || shiftISO(b.from, -364), pt = b.pt || shiftISO(b.to, -364);
      return { label: b.label, oms: sum('oms', b.from, b.to), omsP: sum('oms', pf, pt), kop: sum('kop', b.from, b.to), kopP: sum('kop', pf, pt) };
    });

    const tot = rows.reduce((a, r) => ({ oms: a.oms + r.oms, omsP: a.omsP + r.omsP, kop: a.kop + r.kop, kopP: a.kopP + r.kopP }), { oms: 0, omsP: 0, kop: 0, kopP: 0 });
    const graf = rows.map((r) => {
      if (matt === 'kop')   return { label: r.label, value: r.kop, prev: r.kopP > 0 ? r.kopP : null };
      if (matt === 'snitt') return { label: r.label, value: r.kop > 0 ? Math.round(r.oms / r.kop) : null, prev: r.kopP > 0 ? Math.round(r.omsP / r.kopP) : null };
      return { label: r.label, value: r.oms, prev: r.omsP > 0 ? r.omsP : null };
    });
    // Tabellrader: senaste först, tomma perioder (varken i år eller ifjol) utelämnas
    const tabell = [...rows].reverse().filter((r) => r.oms > 0 || r.omsP > 0 || r.kop > 0);
    return { graf, tabell, tot, fromStr, endStr };
  }, [byDay, period, gran, matt, custom, fromSel, toSel]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtVal = matt === 'kop' ? (v) => `${Math.round(v)} köp` : (v) => kr(v);
  const t = data.tot;

  return (
    <>
      {/* KPI:er för vald period */}
      <div className="grid-3">
        <Metric label="Omsättning" value={t.oms > 0 ? kr(t.oms) : '–'}
          sub={<>{`${data.fromStr} → ${data.endStr}`}<FjolRad cur={t.oms} fjol={t.omsP} /></>} />
        <Metric label="Antal köp" value={t.kop > 0 ? t.kop.toLocaleString('sv-SE') : '–'}
          sub={<FjolRad cur={t.kop} fjol={t.kopP} suffix="köp" />} />
        <Metric label="Snittkvitto" value={t.kop > 0 ? kr(t.oms / t.kop) : '–'}
          sub={t.kopP > 0 ? `Ifjol ${kr(t.omsP / t.kopP)}` : 'Omsättning ÷ köp'} />
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Försäljning — i år vs ifjol</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <button onClick={() => setMatt('oms')}   style={chip(matt === 'oms')}>Omsättning</button>
            <button onClick={() => setMatt('kop')}   style={chip(matt === 'kop')}>Antal köp</button>
            <button onClick={() => setMatt('snitt')} style={chip(matt === 'snitt')}>Snittkvitto</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, fontFamily: 'system-ui,sans-serif' }}>
          {[['30d', '30 dagar'], ['90d', '90 dagar'], ['12m', '12 månader']].map(([id, lab]) => (
            <button key={id} onClick={() => { setPeriod(id); setFromSel(''); setToSel(''); }} style={chip(period === id && !custom)}>{lab}</button>
          ))}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 3px' }} />
          {[['dag', 'Dag'], ['vecka', 'Vecka'], ['manad', 'Månad']].map(([id, lab]) => (
            <button key={id} onClick={() => setGran(id)} style={chip(gran === id)}>{lab}</button>
          ))}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 3px' }} />
          <input type="date" value={fromSel} onChange={(e) => setFromSel(e.target.value)} style={dateInp} aria-label="Från datum" />
          <span style={{ color: 'var(--text-secondary)', fontSize: 11.5 }}>–</span>
          <input type="date" value={toSel} onChange={(e) => setToSel(e.target.value)} style={dateInp} aria-label="Till datum" />
          {custom && <button onClick={() => { setFromSel(''); setToSel(''); }} style={chip(false)}>Rensa</button>}
        </div>
        <TrendCompare data={data.graf} formatVal={fmtVal} curColor={COLOR} prevColor="#C2A878" />
        <div className="note">
          Ifjol = samma veckodag (−364 dgr); månad = samma kalendermånad. Egen datumperiod väljs med datumfälten.
        </div>
      </div>

      {/* Statistiktabell — samma perioder som diagrammet */}
      <div className="card">
        <div className="card-title">Statistik per {gran === 'dag' ? 'dag' : gran === 'vecka' ? 'vecka' : 'månad'} — {data.fromStr} → {data.endStr}</div>
        <div className="period-table">
          <div className="period-header period-row-6">
            <span>{gran === 'dag' ? 'Dag' : gran === 'vecka' ? 'Vecka' : 'Månad'}</span>
            <span>Omsättning</span><span>Ifjol</span><span>±%</span><span>Köp</span><span>Snittkvitto</span>
          </div>
          {data.tabell.map((r, i) => {
            const diff = r.omsP > 0 ? Math.round((r.oms - r.omsP) / r.omsP * 100) : null;
            return (
              <div key={i} className="period-row period-row-6">
                <span>{r.label}</span>
                <span>{r.oms > 0 ? kr(r.oms) : '–'}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{r.omsP > 0 ? kr(r.omsP) : '–'}</span>
                <span style={{ color: diff === null ? 'var(--text-secondary)' : diff >= 0 ? '#3B6D11' : '#A32D2D', fontWeight: 600 }}>
                  {diff === null ? '–' : `${diff >= 0 ? '+' : ''}${diff}%`}
                </span>
                <span>{r.kop > 0 ? `${r.kop.toLocaleString('sv-SE')}${r.kopP > 0 ? ` (${r.kopP})` : ''}` : '–'}</span>
                <span>{r.kop > 0 ? kr(r.oms / r.kop) : '–'}</span>
              </div>
            );
          })}
          {data.tabell.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '10px 0', fontFamily: 'system-ui,sans-serif' }}>Ingen försäljning i perioden.</div>}
          <div className="period-row period-row-6" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
            <span>Summa</span>
            <span>{t.oms > 0 ? kr(t.oms) : '–'}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{t.omsP > 0 ? kr(t.omsP) : '–'}</span>
            <span style={{ fontWeight: 700, color: t.omsP > 0 ? (t.oms >= t.omsP ? '#3B6D11' : '#A32D2D') : 'var(--text-secondary)' }}>
              {t.omsP > 0 ? `${t.oms >= t.omsP ? '+' : ''}${Math.round((t.oms - t.omsP) / t.omsP * 100)}%` : '–'}
            </span>
            <span>{t.kop > 0 ? `${t.kop.toLocaleString('sv-SE')}${t.kopP > 0 ? ` (${t.kopP})` : ''}` : '–'}</span>
            <span>{t.kop > 0 ? kr(t.oms / t.kop) : '–'}</span>
          </div>
        </div>
        <div className="note">Köp inom parentes = ifjol. Ifjol = samma veckodag (−364 dgr) resp. samma kalendermånad.</div>
      </div>
    </>
  );
}

// ── ÖVERSIKT (snabbkort, 30-dagarsgraf, varugrupper, betalsätt) ────
function Oversikt({ data }) {
  const [vgPeriod, setVgPeriod] = useState('vecka');

  const D = useMemo(() => {
    if (!data || !Array.isArray(data.days)) return null;
    const byDay = {};
    for (const [datum, oms, marg, kvitton] of data.days) byDay[datum] = { oms, marg, kvitton };
    const today = data.days.length ? data.days[data.days.length - 1][0] : null;
    if (!today) return null;

    const sum = (from, to) => {
      let oms = 0, kvitton = 0;
      for (let d = from; d <= to; d = shiftISO(d, 1)) { const v = byDay[d]; if (v) { oms += v.oms; kvitton += v.kvitton; } }
      return { oms, kvitton };
    };
    const igar = shiftISO(today, -1);
    const monthStart = today.slice(0, 8) + '01';
    // Ifjol slås upp i samma fulla historik (−364 = samma veckodag)
    const mk = (from, to) => ({ cur: sum(from, to), fjol: sum(shiftISO(from, -364), shiftISO(to, -364)) });

    return {
      today,
      idag: mk(today, today),
      igarD: mk(igar, igar),
      v7: mk(shiftISO(today, -6), today),
      manad: mk(monthStart, today),
      graf: Array.from({ length: 30 }, (_, i) => {
        const d = shiftISO(today, i - 29);
        const f = byDay[shiftISO(d, -364)];
        return {
          label: `${Number(d.slice(8, 10))}/${Number(d.slice(5, 7))}`,
          value: byDay[d] ? byDay[d].oms : 0,
          prev: f ? f.oms : null,
        };
      }),
    };
  }, [data]);

  const kort = (label, mkObj, sub) => {
    const { cur, fjol } = mkObj;
    return (
      <Metric
        label={label}
        value={cur.oms !== 0 ? kr(cur.oms) : '–'}
        sub={<>
          {cur.kvitton > 0 ? `${cur.kvitton} köp · snitt ${kr(cur.oms / cur.kvitton)}` : (sub || '')}
          <FjolRad cur={cur.oms} fjol={fjol.oms} />
        </>}
      />
    );
  };

  const vg = data?.varugrupper?.[vgPeriod] || [];
  const maxVg = Math.max(...vg.map(g => g.belopp), 1);
  const bs = data?.betalsatt || [];
  const maxBs = Math.max(...bs.map(b => b.belopp), 1);
  const m7 = data?.marginal7 || null;
  const besok = data?.besok || {};

  if (!D) return null;
  return (
    <>
      {/* Snabbkort */}
      <div className="grid-4">
        {kort('Idag', D.idag, 'Inga köp än idag')}
        {kort('Igår', D.igarD)}
        {kort('Senaste 7 dagarna', D.v7)}
        {kort('Hittills i månad', D.manad)}
      </div>

      {/* Statusrad */}
      <div className="grid-4">
        <Metric label="Insläpp (BankID)"
          value={besok.idag != null ? `${besok.idag} idag` : '–'}
          sub={besok.igar != null ? `Igår ${besok.igar} insläpp` : 'Identifieringar vid dörren'} />
        <Metric label="Marginal (7 dgr)"
          value={m7 ? `${m7.pct}%` : '–'}
          sub={m7 ? `${kr(m7.belopp)} · est. via inpris (${m7.tackning}% täckning)` : 'Inpris saknas på artiklarna'} />
        <Metric label="Lagervärde"
          value={data.lager ? kr(data.lager.varde) : '–'}
          sub={data.lager ? `${data.lager.antalArtiklar} artiklar (inpris)` : ''} />
        <Metric label="Att fylla på"
          value={data.lager ? String((data.lager.pafyllnad || []).length) : '–'}
          sub="Artiklar vid beställningspunkt"
          trend={data.lager && data.lager.pafyllnad.length > 10 ? 'down' : ''} />
      </div>

      {/* 30-dagarsgraf */}
      <div className="card">
        <div className="card-title" style={{ marginBottom: 2 }}>Omsättning per dag — senaste 30 dagarna vs ifjol</div>
        <TrendCompare data={D.graf} formatVal={(v) => kr(v)} curColor={COLOR} prevColor="#C2A878" />
        <div className="note">Ifjol = samma veckodag (−364 dgr). Källa: HonestBox (uppdateras var 15:e min).</div>
      </div>

      {/* Varugrupper + betalsätt */}
      <div className="grid-2">
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div className="card-title" style={{ marginBottom: 0 }}>Topp varugrupper</div>
            <div style={{ display: 'flex', gap: 5 }}>
              <button onClick={() => setVgPeriod('vecka')} style={chip(vgPeriod === 'vecka')}>7 dagar</button>
              <button onClick={() => setVgPeriod('manad')} style={chip(vgPeriod === 'manad')}>30 dagar</button>
            </div>
          </div>
          {vg.map((g, i) => (
            <div key={i} style={{ marginBottom: 7, fontFamily: 'system-ui,sans-serif' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 2 }}>
                <span>{g.namn}</span><span style={{ fontWeight: 600 }}>{kr(g.belopp)}</span>
              </div>
              <div style={{ height: 4, background: 'var(--panel2)', borderRadius: 2 }}>
                <div style={{ height: '100%', width: `${Math.round(g.belopp / maxVg * 100)}%`, background: COLOR, borderRadius: 2 }} />
              </div>
            </div>
          ))}
          {vg.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen försäljning i perioden.</div>}
        </div>

        <div className="card">
          <div className="card-title">Betalsätt — senaste 30 dagarna</div>
          {bs.map((b, i) => (
            <div key={i} style={{ marginBottom: 7, fontFamily: 'system-ui,sans-serif' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 2 }}>
                <span>{b.metod}</span><span style={{ fontWeight: 600 }}>{kr(b.belopp)}</span>
              </div>
              <div style={{ height: 4, background: 'var(--panel2)', borderRadius: 2 }}>
                <div style={{ height: '100%', width: `${Math.round(b.belopp / maxBs * 100)}%`, background: COLOR, borderRadius: 2 }} />
              </div>
            </div>
          ))}
          {bs.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen data.</div>}
        </div>
      </div>

      {/* Påfyllnadslista */}
      {data.lager && (data.lager.pafyllnad || []).length > 0 && (
        <div className="card">
          <div className="card-title">Dags att fylla på — saldo vid beställningspunkten</div>
          <div className="period-table">
            <div className="period-header period-row-4">
              <span>Artikel</span>
              <span style={{ textAlign: 'right' }}>Saldo</span>
              <span style={{ textAlign: 'right' }}>Best.punkt</span>
              <span style={{ textAlign: 'right' }}>Max</span>
            </div>
            {data.lager.pafyllnad.map((a, i) => (
              <div key={i} className="period-row period-row-4">
                <span style={{ fontWeight: a.saldo === 0 ? 700 : 400, color: a.saldo === 0 ? '#A32D2D' : 'var(--text)' }}>{a.namn}{a.saldo === 0 ? ' — SLUT' : ''}</span>
                <span style={{ textAlign: 'right', fontWeight: 600, color: a.saldo === 0 ? '#A32D2D' : 'inherit' }}>{a.saldo}</span>
                <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{a.punkt}</span>
                <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{a.max || '–'}</span>
              </div>
            ))}
          </div>
          <div className="note">Artiklar där lagersaldot nått beställningspunkten (max 30 visas). Källa: HonestBox lagersaldo.</div>
        </div>
      )}
    </>
  );
}

export default function LanthandelBlock() {
  const [sub, setSub] = useState('oversikt');
  const [data, setData] = useState(undefined);
  const [exMoms, setExMoms] = useState(false);
  useEffect(() => { getLanthandel().then(setData).catch(() => setData(null)); }, []);

  const TABS = [
    { id: 'oversikt',   label: 'Översikt'   },
    { id: 'produkter',  label: 'Produkter'  },
    { id: 'forsaljning', label: 'Försäljning' },
  ];

  // HonestBox levererar allt inkl moms. Budgeten är ex moms, så växeln finns
  // för att kunna jämföra utan att räkna om i huvudet. Omräkningen sker på
  // DATAN, inte i varje vy — då följer grafer, tabeller och kort med
  // automatiskt. Marginalen rörs inte, den är redan ex moms.
  const sats = data?.moms?.sats ?? null;
  const faktor = exMoms && sats ? 1 + sats / 100 : 1;
  const visad = useMemo(() => {
    if (!data || faktor === 1) return data;
    const d = (v) => Math.round((v || 0) / faktor);
    const grupper = (g) => (g || []).map((x) => ({ ...x, belopp: d(x.belopp) }));
    return {
      ...data,
      days: (data.days || []).map(([datum, oms, marg, kvitton]) => [datum, d(oms), marg, kvitton]),
      summering: data.summering && {
        ...data.summering,
        oms90: d(data.summering.oms90),
        snittkvitto90: d(data.summering.snittkvitto90),
      },
      varugrupper: data.varugrupper && { vecka: grupper(data.varugrupper.vecka), manad: grupper(data.varugrupper.manad) },
      betalsatt: grupper(data.betalsatt),
    };
  }, [data, faktor]);

  return (
    <div className="screen">
      <div className="header header-lanthandel">
        <div>
          <div className="header-title">Stigmyhrs Lanthandel — Ramundberget · Obemannad butik</div>
          <div className="header-name">Försäljning & drift</div>
        </div>
        <Clock />
      </div>
      <SubNav tabs={TABS} active={sub} onChange={setSub} />

      {sub === 'oversikt' && <MalKort enheter={['Lanthandeln']} />}

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 10px 2px', fontFamily: 'system-ui,sans-serif' }}>
        <button onClick={() => setExMoms(false)} style={chip(!exMoms)}>Inkl. moms</button>
        <button onClick={() => setExMoms(true)} style={chip(exMoms)} disabled={!sats}
          title={sats ? `Blandad sats ${String(sats).replace('.', ',')} % räknad ur senaste veckans artikelrader`
            : 'Momssatsen kunde inte räknas fram ur artikelraderna'}>
          Ex. moms{sats ? ` (${String(sats).replace('.', ',')} %)` : ''}
        </button>
        {exMoms && (
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            Jämförbart med budgeten · sats ur {data?.moms?.rader} artikelrader senaste veckan
          </span>
        )}
      </div>

      {data === undefined && sub !== 'produkter' && <div className="card"><div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar data från HonestBox…</div></div>}
      {data === null && sub !== 'produkter' && <div className="card"><div style={{ fontSize: 13, color: '#A32D2D', fontFamily: 'system-ui,sans-serif' }}>Ingen data ännu — cachen fylls vid nästa hämtning (var 15:e min). Admin kan trycka ↻.</div></div>}

      {sub === 'oversikt' && visad && <Oversikt data={visad} />}
      {sub === 'produkter' && <ProdukterTab faktor={faktor} />}
      {sub === 'forsaljning' && visad && <ForsaljningTab days={visad.days} />}
    </div>
  );
}
